import { useState, type FormEvent } from 'react'
import { api, type ApiError } from '../lib/api'
import { navigate } from '../lib/router'
import { AuthLayout } from '../components/auth/AuthLayout'
import { FloatingInput } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { StatusBox } from '../components/ui/badge'

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!token) {
      setError('Missing reset token. Please use the link from your email.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await api.resetPassword(token, password)
      setDone(true)
    } catch (err) {
      setError((err as ApiError).title ?? 'Failed to reset password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title="Set new password"
      subtitle={done || !token ? undefined : 'Choose a new password for your account.'}
      panel={{ line1: 'Almost there.', line2: 'Pick a password only you know.' }}
    >
      {done ? (
        <div className="space-y-4">
          <StatusBox tone="success">Your password has been reset. You can now log in.</StatusBox>
          <Button size="auth" onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </div>
      ) : !token ? (
        <div className="space-y-4">
          <StatusBox tone="destructive">Invalid or missing reset link. Please request a new one.</StatusBox>
          <Button size="auth" onClick={() => navigate('/forgot-password')}>
            Request new link
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit}>
          <FloatingInput
            label="New password"
            type="password"
            size="auth"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            hint="Min. 8 characters"
          />
          <FloatingInput
            label="Confirm password"
            type="password"
            size="auth"
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />

          {error && <StatusBox tone="destructive">{error}</StatusBox>}

          <Button type="submit" size="auth" disabled={submitting}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
